/* Copyright (c) 2020-present CloudIO Inc */

import { IoConnections, Query } from '@cloudio-saas/datasource-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Link,
} from '@mui/material';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { activeSidebarPanelAtom } from 'layout/Sidebar/atoms';
import { usePending, usePlatform } from 'platform/PlatformHooks';
import React, { memo, useEffect, useState } from 'react';
import { useStoreBusy } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import WaveDots from 'utils/WaveDots';
import { ConnectionEditBody } from '../ConnectionsView/ConnectionEdit';
import { DBRow, isFromDB } from 'store/storeTypes';
import Icon from 'icons/Icon';

export const dsFilter: () => Query<IoConnections> = () => ({
  filter: [],
  limit: 5000,
  offset: 0,
  projection: {
    connectionName: 1,
    connectionUid: 1,
  },
});

function ConnectionSelect({
  appUid,
  disabled,
  label,
  onChange,
  required,
  value,
  'data-testid': dataTestId,
  hideCreateNew,
  width,
}: {
  appUid: string;
  disabled?: boolean;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  value: string;
  'data-testid': string;
  hideCreateNew?: boolean;
  width?: number;
}) {
  const store = useDataStore<IoConnections>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoConnections',
    alias: `IoConnections_select_alias_${appUid}`,
  });
  useEffect(() => {
    store.query({ props: dsFilter() });
  }, [appUid, store]);
  const records = useStoreRecords(store);
  const busy = useStoreBusy(store);
  const platform = usePlatform();
  const handleChange = (roleUid: string, option: Option | null) => {
    if (roleUid !== value) {
      onChange(roleUid);
    }
  };
  const [open, setOpen] = useState(false);
  const [isPending, setPending] = usePending();
  const conStore = useDataStore<IoConnections>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoConnections',
    alias: 'app_add_connection_alias',
  });

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    platform.startAsyncTransition(async () => {
      await conStore.createNew({
        partialRecord: { dbType: 'MySQL' },
        rs: 'I',
      });
    }, setPending);
    setOpen(true);
  };

  return (
    <>
      <FormControl
        component="fieldset"
        disabled={disabled}
        fullWidth
        required={required}
        variant="standard"
      >
        {records.length ? (
          <VirtualizedSelectInput
            data-testid={dataTestId}
            disabled={busy || disabled}
            label={label}
            required={required}
            labelAttribute="connectionName"
            onChange={handleChange}
            options={records}
            placeholder={busy ? 'Loading ...' : 'Choose a connection'}
            startIcon={busy ? <WaveDots /> : null}
            value={value}
            valueAttribute="connectionUid"
            variant="standard"
            endIcon={
              isPending ? (
                <WaveDots />
              ) : !hideCreateNew ? (
                <Icon iconName="plus" onClick={handleClick}></Icon>
              ) : (
                ''
              )
            }
          />
        ) : (
          <Link
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              platform.setter(activeSidebarPanelAtom, 'connections');
            }}
          >
            Setup Connection
          </Link>
        )}
      </FormControl>
      <Dialog open={open} maxWidth="xl">
        <DialogTitle></DialogTitle>
        <DialogContent
          sx={{ maxHeight: '700px', height: '800px', width: '500px' }}
        >
          <ConnectionEditBody appUid={appUid} store={conStore} width={500} />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpen(false);
              conStore.reset();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const row = conStore.getCurrentRecord();
              if (
                !(row?.connectionUid && row?.connectionName && row.databaseUrl)
              ) {
                platform.showError('You must fill-in all mandatory fields!');
                return;
              }
              const fromDB = isFromDB(row);
              conStore.save({ feedback: 'NONE' }).then(async (r) => {
                if (r) {
                  let feedback = '';
                  if (fromDB) {
                    feedback = `Connection ${row?.connectionUid} updated successfully!`;
                  } else if (row) {
                    const dbrow =
                      conStore.getCurrentRecord() as DBRow<IoConnections>;

                    feedback = `Connection ${dbrow.connectionUid} created successfully!`;
                  }
                  platform.showSuccess(feedback);
                  store.refresh();
                  setOpen(false);
                }
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(ConnectionSelect);
